import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAvailabilityStoresStart } from '@redux/actions/bopis/getAvailabilityStores';
import { updateCartLine, updateSOCartLine } from '@redux/actions/cartDetail';
import SitecoreContextFactoryService from '@services/sitecoreContextFactoryService';
import {
  ASSEMBLE_OPTION_KEY,
  DEFAULT_PRODUCT_QUANTITY,
  DELIVERY_OPTION,
  GET_AVAILABILITY_STORES_TYPE,
  PromotionType
} from '@utils/constant';
import { bopisThresholdCalculate, deepCopy } from '@utils/utility';

import PromotionCartUpdateModel from '../models/PromotionCartUpdateModel';

export const useUpdateDeliveryOption = () => {
  const dispatch = useDispatch();
  const isClientTelingUser = SitecoreContextFactoryService.getValueContextItem('isClientTelingUser');
  const currentCartLines = useSelector(state => state.productReducer.cartDetailReducer.currentCart?.Lines) || [];

  return useCallback((deliveryItem) => {
    if (currentCartLines.length) {
      let updateCartPayload = {
        Lines: []
      };
      for (const line of currentCartLines) {
        const subLinesObject = [];
        const promotionCartLines = [];

        if (line.PromotionCartLines?.length) {
          line.PromotionCartLines.map((promotionItem) => {
            promotionCartLines.push(new PromotionCartUpdateModel().getData(promotionItem || {}));
          });
        }

        if (line.SubLines?.length) {
          line.SubLines.forEach((item) => {
            let bundlePromotion = [];
            if (item.PromotionCartLines && item.PromotionCartLines.length > 0) {
              bundlePromotion = item.PromotionCartLines[0];
            }

            subLinesObject.push({
              ProductId: item.ProductId,
              VariantId: item.VariantId,
              Quantity: line.Quantity,
              PromotionCartLines: [
                {
                  ProductId: bundlePromotion.ProductId,
                  CatalogName: bundlePromotion.Catalog,
                  Type: bundlePromotion.Type,
                  RelationshipId: bundlePromotion.RelationshipId
                }
              ]
            });
          });
        }

        updateCartPayload.Lines.push({
          LineUniqueId: line.ExternalCartLineId,
          ProductId: line.ProductId,
          VariantId: line.VariantId,
          CatalogName: line.Catalog,
          Quantity: line.Quantity,
          PromotionCartLines: promotionCartLines?.length
            ? promotionCartLines
            : new PromotionCartUpdateModel().getData({}),
          DeliveryOptionId: deliveryItem.ExternalId,
          Sublines: [
            ...(
              subLinesObject.length ? subLinesObject : []
            )
          ]
        });
      }

      if (isClientTelingUser) {
        dispatch(updateSOCartLine(updateCartPayload));
      } else {
        dispatch(updateCartLine(updateCartPayload));
      }
    }
  }, [currentCartLines, isClientTelingUser]);
};

export const useMappingDeliveryOptions = () => {
  const currentCart = useSelector(state => state.productReducer.cartDetailReducer.currentCart);
  const currentCartLines = useMemo(() => currentCart?.Lines || [], [currentCart]);

  return useMemo(() => {
    const deliveryOptionsList = [];
    if (currentCartLines.length) {
      /** @param {String} deliveryExternalId */
      const checkDeliveryOptionExistInEveryLine = (deliveryExternalId) => currentCartLines.every((line) => (
        line.DeliveryOptions ?? []
      ).find((deliveryOption) => deliveryOption.ExternalId === deliveryExternalId));

      const isLineHasAssemblyOption = currentCartLines.some(line => line.PromotionCartLines.find((item) => item.RelationshipId
        === ASSEMBLE_OPTION_KEY));

      const isLineHasTradeInOption = currentCartLines.some(line => line.PromotionCartLines.find((item) => item.Type
        === PromotionType.TradeIn
        || item.Type
        === PromotionType.TradeInLoyalty));

      const isLineHasFreeGift = currentCartLines.some(line => line.PromotionCartLines.find((item) => (
        item.Type === PromotionType.FreeGift && !item.HasBOPISOption
      )));
      const isCartHasFreeGift = currentCartLines.some(line => !line.IsEditable && !line?.IsFreeGiftPopup);
      const isCartHasFreeGiftPopup = currentCart?.FreeGiftPopup?.HasFreeGiftPopup;
      const isCartHasGiftOption = currentCartLines.some(line => line.GiftingOptionLines?.length);

      for (const line of currentCartLines) {
        const deliveryOptionsInLine = [...line.DeliveryOptions];
        if (deliveryOptionsInLine.length) {
          for (const deliveryOption of deliveryOptionsInLine) {
            const isDeliveryOptionExistForAllLine = checkDeliveryOptionExistInEveryLine(deliveryOption.ExternalId);

            const isDeliveryOptionExistInList = deliveryOptionsList.find((option) => option.ExternalId
              === deliveryOption.ExternalId);

            if (isDeliveryOptionExistForAllLine && !isDeliveryOptionExistInList) {
              let isDisabledBOPISOption = false;
              if (deliveryOption.ShippingOptionType?.Value === DELIVERY_OPTION.PickUpStore) {
                if (!line.SubLines?.length) {
                  const isEveryLineHasBOPISOption = currentCartLines.every((line) => !line.SubLines?.length
                    ? line.HasBOPISOption
                    : true);

                  isDisabledBOPISOption = !isEveryLineHasBOPISOption
                    || isLineHasAssemblyOption
                    || isLineHasTradeInOption
                    || isLineHasFreeGift
                    || isCartHasFreeGift
                    || isCartHasFreeGiftPopup || isCartHasGiftOption;

                  /** INFO: add checking for bundle in case cart has bundle and normal product */
                  const isCartLinesHaveBundle = currentCartLines.some((line) => line.SubLines?.length);

                  if (!isDisabledBOPISOption && isCartLinesHaveBundle) {
                    isDisabledBOPISOption = !currentCartLines.every(line => line.SubLines?.length
                      ? line.SubLines.every((subLine) => {
                        if (subLine.PromotionCartLines?.length) {
                          return subLine.HasBOPISOption
                            && !subLine.PromotionCartLines.filter((promotionItem) => promotionItem.RelationshipId
                              === ASSEMBLE_OPTION_KEY)?.length;
                        }

                        return subLine.HasBOPISOption;
                      })
                      : true);
                  }
                } else {
                  isDisabledBOPISOption = !line.SubLines.every((subLine) => {
                    if (subLine.PromotionCartLines?.length) {
                      return subLine.HasBOPISOption
                        && !subLine.PromotionCartLines.filter((promotionItem) => promotionItem.RelationshipId
                          === ASSEMBLE_OPTION_KEY)?.length;
                    }

                    return subLine.HasBOPISOption;
                  });
                }
              }

              deliveryOptionsList.push({
                ...deliveryOption,
                isDisabled: isDisabledBOPISOption,
                ExternalCartLineId: line.ExternalCartLineId || '',
                DeliveryOptionId: line.DeliveryOptionId || '',
                IsInStockProduct: line.IsInStockProduct || '',
                InStockDeliveryLabel: line.InStockDeliveryLabel || '',
                IsOutStockProduct: line.IsOutStockProduct || '',
                OutStockDeliveryLabel: line.OutStockDeliveryLabel || '',
                IsPreOrderProduct: line.IsPreOrderProduct || '',
                PreOrderDeliveryLabel: line.PreOrderDeliveryLabel || '',
                IsBackOrderProduct: line.IsBackOrderProduct || '',
                BackOrderDeliveryLabel: line.BackOrderDeliveryLabel || ''
              });
            }
          }
        }
      }
    }

    return deliveryOptionsList;
  }, [currentCartLines, currentCart?.FreeGiftPopup?.HasFreeGiftPopup]);
};

export const useCheckDeliveryOption = () => {
  const currentCartLines = useSelector(state => state.productReducer.cartDetailReducer.currentCart?.Lines) || [];

  return useMemo(() => (
    {
      isBOPISOptionSelected: currentCartLines.some((line) => line.DeliveryOptions?.find((deliveryOption) => deliveryOption?.ExternalId
        === line.DeliveryOptionId)?.ShippingOptionType?.Value === DELIVERY_OPTION.PickUpStore),
      isLineExistOptionNotBOPIS: currentCartLines.some((line) => line?.DeliveryOptions?.find((deliveryOption) => deliveryOption?.ExternalId
        === line.DeliveryOptionId)?.ShippingOptionType?.Value !== DELIVERY_OPTION.PickUpStore)
    }
  ), [currentCartLines]);
};

export const useFetchAvailabilityStores = () => {
  const dispatch = useDispatch();

  const mappingProductsList = useCallback((line) => {
    let productsList = [];
    const defaultThreshold = line.BOPISThreshold;
    const lineQuantity = Number.parseInt(line.Quantity);

    if (line.PromotionCartLines?.length && (
      line.PromotionCartLines.some(item => item.HasBOPISOption)
      || line.PromotionCartLines.some(item => item.RelationshipId)
    )) {
      line.PromotionCartLines.forEach(promotionLine => (
        promotionLine.HasBOPISOption || promotionLine.RelationshipId
      ) ? productsList.push({
          ProductId: promotionLine.ProductId,
          VariantId: promotionLine.VariantId || '',
          Threshold: bopisThresholdCalculate({
            currentThreshold: promotionLine.BOPISThreshold || 0,
            lineQuantity: DEFAULT_PRODUCT_QUANTITY
          })
        }) : null);
    }

    productsList.push({
      ProductId: line.ProductId || '',
      VariantId: line.VariantId || '',
      Threshold: bopisThresholdCalculate({
        lineQuantity,
        currentThreshold: defaultThreshold
      })
    });

    return productsList;
  }, []);

  const handleMergeProducts = useCallback((productsList) => {
    const productsListMap = new Map();
    for (const productLine of deepCopy(productsList)) {
      const mapKey = productLine.ProductId;
      if (!productsListMap.get(mapKey)) {
        productsListMap.set(mapKey, [productLine]);
      } else {
        const productsListInMap = productsListMap.get(mapKey);

        const variantExistInList = productsListInMap.find((item) => item.VariantId
          === productLine.VariantId);

        if (variantExistInList) {
          variantExistInList.Threshold += productLine.Threshold;

          productsListMap.set(mapKey, productsListInMap);
        } else {
          productsListMap.set(mapKey, [...productsListInMap, productLine]);
        }
      }
    }

    return Array.from(productsListMap.values()) || [];
  }, []);

  return useCallback((currentCartLines) => {
    let productsList = [];

    for (const line of currentCartLines) {
      if (line.SubLines?.length) {
        if (line.PromotionFreeGift?.length){
          for (const freeGift of line.PromotionFreeGift) {
            productsList = [...productsList, ...mappingProductsList(freeGift)];
          }
        }
        for (const subLine of line.SubLines) {
          productsList = [...productsList, ...mappingProductsList(subLine)];
        }
      } else {
        productsList = [...productsList, ...mappingProductsList(line)];
      }
    }

    dispatch(getAvailabilityStoresStart({
      ListProducts: [...handleMergeProducts(productsList).flat()],
      Type: GET_AVAILABILITY_STORES_TYPE.And
    }));
  }, [mappingProductsList]);
};